'use client';

import { useEffect, useState, useMemo } from 'react';
import SpiderChart from './SpiderChart'; // Assuming SpiderChart can accept answers directly
import { useRouter } from 'next/navigation';
// Import dimensions from benchmarks.ts
import { dimensions, industryAverageData } from '../data/benchmarks';
// Import the new component
import ResultListItem from './ResultListItem';
import { generatePDFReport } from './PDFReport';
import ChartGenerator from './ChartGenerator';
import DemoLeadCaptureModal from '@/app/demo/try/DemoLeadCaptureModal';

interface UserInfo {
  company?: string;
  country?: string;
  therapyArea?: string;
  functionalArea?: string;
  name?: string;
  email?: string;
}

interface ResultDisplayProps {
  initialAnswers: number[];
  initialTimes?: number[];
  userInfo?: UserInfo | null;
  // Remove dimensions from props if always using the imported one
  // dimensions: string[];
}

// Type the dimensions explicitly (or infer from import if TS setup allows)
const typedDimensions: (string | string[])[] = dimensions;

// Remove local definitions
// const industryAverageData = [73, 61, 46, 51, 46, 52, 48];

// Pass initialAnswers directly, use imported dimensions/industryAverageData
export default function ResultDisplay({ initialAnswers, initialTimes: propInitialTimes = [], userInfo }: ResultDisplayProps) {
  const router = useRouter();
  const [resultId, setResultId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showContactForm, setShowContactForm] = useState(false); // Skip contact form since we already have user info
  const [contactInfo, setContactInfo] = useState({ 
    name: userInfo?.name || '', 
    email: userInfo?.email || '' 
  });
  const [isUpdatingContact, setIsUpdatingContact] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [emailValid, setEmailValid] = useState(true);
  const [pdfGateOpen, setPdfGateOpen] = useState(false);
  const answers = initialAnswers;
  const scores = useMemo(() => initialAnswers.map((answer) => (answer + 1) * 25), [initialAnswers]);
  const totalScore = useMemo(() => scores.reduce((sum, score) => sum + score, 0), [scores]);
  const overallRating = useMemo(() => (scores.length > 0 ? Math.round(totalScore / scores.length) : 0), [scores, totalScore]);
  const [chartImageBase64, setChartImageBase64] = useState<string | null>(null);

  const initialTimes = useMemo(() => {
    if (propInitialTimes && propInitialTimes.length > 0) {
      return propInitialTimes;
    }
    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem('assessmentTimes');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) return parsed;
        } catch {}
      }
    }
    return [];
  }, [propInitialTimes]);

  // Automatically submit results when component loads
  useEffect(() => {
    console.log('[results] mount', {
      answersLen: answers.length,
      scores,
      initialTimes,
      totalScore,
      overallRating,
    });

    const submitResults = async () => {
      console.log('[results] submitting to /api/submit-results ...');
      try {
        setIsSubmitting(true);
        setSubmitError(null);

        let completionTime = 0;
        const start = sessionStorage.getItem('assessmentStartTime');
        if (start) {
          completionTime = Math.floor((Date.now() - parseInt(start, 10)) / 1000);
        }
        if (!completionTime && initialTimes.length) {
          completionTime = initialTimes.reduce(
            (sum: number, t: number) => sum + (typeof t === 'number' ? t : 0),
            0,
          );
        }

        const payload = {
          score_1: scores[0], score_2: scores[1], score_3: scores[2],
          score_4: scores[3], score_5: scores[4], score_6: scores[5], score_7: scores[6],
          q1_time: initialTimes[0] ?? null, q2_time: initialTimes[1] ?? null, q3_time: initialTimes[2] ?? null,
          q4_time: initialTimes[3] ?? null, q5_time: initialTimes[4] ?? null, q6_time: initialTimes[5] ?? null,
          q7_time: initialTimes[6] ?? null,
          total_score: totalScore, average_score: overallRating,
          completion_time: completionTime || 0,
          device_type: window.innerWidth < 768 ? 'mobile' : 'desktop',
          browser: navigator.userAgent,
          // Include user info if available
          name: userInfo?.name || null,
          email: userInfo?.email || null,
          company: userInfo?.company || null,
          country: userInfo?.country || null,
          therapy_area: userInfo?.therapyArea || null,
          functional_area: userInfo?.functionalArea || null
        };
        console.log('[results] payload', payload);

        const response = await fetch('/api/submit-results', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        console.log('[results] response status', response.status);
        if (!response.ok) {
          const errorText = await response.text();
          console.error('[results] Response not ok:', errorText);
          throw new Error(`Failed to submit results. Status: ${response.status}`);
        }
        
        const data = (await response.json()) as { success?: boolean; resultId?: number; error?: string; message?: string };
        console.log('[results] response json', data);
        if (!data.success) {
          console.error('[results] API returned success=false:', data.error);
          throw new Error(data.error || 'Failed to process submission');
        }
        if (!data.resultId && data.resultId !== 0) {
          console.error('[results] No resultId returned:', data);
          throw new Error('No result ID returned from server');
        }
        
        // Handle development mode where database is not available
        if (data.resultId === 0 && data.message?.includes('database not available')) {
          console.log('[results] Development mode detected - database not available, proceeding without resultId');
          setResultId(null); // Explicitly set to null for development mode
        } else {
          setResultId(data.resultId);
          sessionStorage.setItem('resultId', data.resultId.toString());
        }

        sessionStorage.removeItem('assessmentStartTime');
        console.log('[results] stored new id', data.resultId);

        // Note: Email will be sent automatically when chart is ready via useEffect

      } catch (error) {
        console.error('Error submitting results:', error);
        setSubmitError(error instanceof Error ? error.message : 'An unknown error occurred');
        // Even if initial submission fails, allow user to continue with the results view
        console.log('[results] Initial submission failed but allowing user to view results');
      } finally {
        setIsSubmitting(false);
      }
    };

    submitResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // This effect now ONLY handles resetting the contact form's visual state
    setShowContactForm(false); // Keep contact form hidden since we already have user info
    setUpdateSuccess(false);
    setContactInfo({ 
      name: userInfo?.name || '', 
      email: userInfo?.email || '' 
    });
  }, [initialAnswers, userInfo]);

  // Auto-send email when chart becomes available and user has email
  useEffect(() => {
    if (chartImageBase64 && userInfo?.email && validateEmail(userInfo.email)) {
      console.log('[chart-effect] Chart ready, auto-sending email to:', userInfo.email);
      sendEmailReport(userInfo.name || 'User', userInfo.email).catch(error => {
        console.error('[chart-effect] Auto-email failed:', error);
      });
    }
  }, [chartImageBase64, userInfo?.email, userInfo?.name]);

  const handleRestart = () => {
    try {
      sessionStorage.removeItem('assessmentStep');
      sessionStorage.removeItem('assessmentCurrentQuestion');
      sessionStorage.removeItem('assessmentAnswers');
      sessionStorage.removeItem('assessmentCompleted');
      sessionStorage.removeItem('hasSubmitted');
      sessionStorage.removeItem('assessmentStartTime');
    } catch (error) {
      console.error('Error clearing session storage:', error);
    }

    // Navigate to the benchmark page to start over
    router.push('/benchmark/assessment');
  };



  // Calculate strengths and improvements once
  const strengths = typedDimensions
    .map((dim, idx) => ({
      dimension: dim,
      index: idx,
      score: scores[idx],
      avg: industryAverageData[idx],
    }))
    .filter((item) => item.score > item.avg);

  const improvements = typedDimensions
    .map((dim, idx) => ({
      dimension: dim,
      index: idx,
      score: scores[idx],
      avg: industryAverageData[idx],
    }))
    .filter((item) => item.score <= item.avg);

  const handleDownloadPDF = async () => {
    const email = (contactInfo.email || userInfo?.email || '').trim();
    if (!email || !validateEmail(email)) {
      setPdfGateOpen(true);
      return;
    }
    try {
      await generatePDFReport(chartImageBase64 || '', overallRating, strengths, improvements, false);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF report. Please try again.');
    }
  };

  const handlePdfIdentitySubmit = async (data: { email: string; name?: string }) => {
    const next = { name: data.name || '', email: data.email.trim() };
    setContactInfo(next);
    setPdfGateOpen(false);
    try {
      sessionStorage.setItem(
        'assessmentUserInfo',
        JSON.stringify({
          ...(userInfo || {}),
          email: next.email,
          name: next.name || undefined,
        }),
      );
    } catch {
      /* ignore */
    }
    if (resultId) {
      try {
        await fetch('/api/submit-results/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            resultId,
            name: next.name || null,
            email: next.email,
          }),
        });
      } catch (err) {
        console.warn('[pdf-gate] contact update failed', err);
      }
    }
    try {
      await generatePDFReport(chartImageBase64 || '', overallRating, strengths, improvements, false);
      if (validateEmail(next.email)) {
        void sendEmailReport(next.name || 'User', next.email).catch(() => undefined);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF report. Please try again.');
    }
  };

  const handleDownloadWhitepaper = () => {
    try {
      // Create a link element to trigger download of the whitepaper PDF
      const link = document.createElement('a');
      link.href = '/dj-7-dimensions-whitepaper-2025.pdf';
      link.download = 'dj-7-dimensions-whitepaper-2025.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading whitepaper:', error);
      alert('Failed to download whitepaper. Please try again.');
    }
  };

  const validateEmail = (email: string) => {
    // Simple email regex
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setContactInfo(prev => ({ ...prev, [id]: value }));
    if (id === 'email') {
      setEmailValid(value === '' || validateEmail(value));
    }
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (contactInfo.email && !validateEmail(contactInfo.email)) {
      setEmailValid(false);
      return;
    }
    try {
      setIsUpdatingContact(true);
      setUpdateError(null);

      console.log('[contact] Starting contact submit, resultId:', resultId);

      // If we have a resultId, try to update the database record
      if (resultId) {
        console.log('[contact] Updating database with contact info');
        try {
          const response = await fetch('/api/submit-results/contact', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              resultId: resultId,
              name: contactInfo.name,
              email: contactInfo.email,
            }),
          });

          console.log('[contact] Database update response status:', response.status);
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error('[contact] Database update failed:', errorText);
            throw new Error(`Failed to update contact information. Status: ${response.status}`);
          }
          
          const data = (await response.json()) as { success?: boolean; error?: string };
          console.log('[contact] Database update response data:', data);
          
          if (!data.success) {
            throw new Error(data.error || 'Failed to update contact information.');
          }
        } catch (dbError) {
          console.error('[contact] Database update error:', dbError);
          throw new Error(`Database update failed: ${dbError instanceof Error ? dbError.message : 'Unknown error'}`);
        }
      } else {
        console.log('[contact] No resultId available - development mode or initial submission failed');
        setUpdateError('Development mode: Database not available. Email functionality will still work in production.');
      }

      // Generate and send PDF report if email is provided
      if (contactInfo.email && validateEmail(contactInfo.email)) {
        console.log('[contact] Attempting to send email report');
        
        // Check if we're in development mode (localhost)
        const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        
        if (isDevelopment) {
          console.log('[contact] Development mode detected - skipping external email API due to CORS');
          setUpdateError('Development mode: Email sending skipped. Your information has been saved.');
        } else {
          try {
            const pdfBase64 = await generatePDFReport(chartImageBase64 || '', overallRating, strengths, improvements, true);
            if (pdfBase64) {
              console.log('[contact] PDF generated, sending email...');
              const reportResponse = await fetch('https://email-api.datajukebox.com', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  type: 'benchmark',
                  name: contactInfo.name || 'Test User',
                  email: contactInfo.email,
                  pdfBase64,
                }),
              });

              console.log('[contact] Email API response status:', reportResponse.status);

              if (!reportResponse.ok) {
                const errorText = await reportResponse.text();
                console.error('[contact] Email API failed:', errorText);
                // Don't throw here - let the user proceed even if email fails
                console.warn('[contact] Email sending failed but allowing user to continue');
                setUpdateError('Email sending failed, but your information has been saved.');
              } else {
                console.log('[contact] Email sent successfully');
              }
            }
          } catch (reportError) {
            console.error('[contact] Email generation/sending error:', reportError);
            // Don't throw here - let the user proceed even if email fails  
            console.warn('[contact] Email generation/sending failed but allowing user to continue');
            setUpdateError('Email sending failed, but your information has been saved.');
          }
        }
      } else {
        console.log('[contact] No email provided, skipping email sending');
      }

      console.log('[contact] Contact submit completed successfully');
      setUpdateSuccess(true);
      setTimeout(() => {
        setShowContactForm(false);
      }, 2000); // Hide after 2 seconds
    } catch (error) {
      console.error('Error updating contact:', error);
      setUpdateError(error instanceof Error ? error.message : 'An unexpected error occurred.');
    } finally {
      setIsUpdatingContact(false);
    }
  };

  const sendEmailReport = async (name: string, email: string) => {
    try {
      console.log('[email] Starting auto-email process...');
      console.log('[email] Chart image available:', !!chartImageBase64);
      console.log('[email] Overall rating:', overallRating);
      console.log('[email] Strengths count:', strengths.length);
      console.log('[email] Improvements count:', improvements.length);
      
      if (!chartImageBase64) {
        throw new Error('Chart image not yet generated');
      }
      
      console.log('[email] Generating PDF report for auto-email...');
      const pdfBase64 = await generatePDFReport(chartImageBase64, overallRating, strengths, improvements, true);
      
      if (pdfBase64) {
        console.log('[email] PDF generated successfully, size:', pdfBase64.length);
        console.log('[email] Sending email to:', email);
        
        const emailPayload = {
          type: 'benchmark',
          name: name,
          email: email,
          pdfBase64: pdfBase64.substring(0, 100) + '...', // Log first 100 chars for debugging
        };
        console.log('[email] Email payload prepared:', emailPayload);
        
        const reportResponse = await fetch('https://email-api.datajukebox.com', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'benchmark',
            name: name,
            email: email,
            pdfBase64,
          }),
        });

        console.log('[email] Email API response status:', reportResponse.status);
        console.log('[email] Email API response headers:', Object.fromEntries(reportResponse.headers.entries()));

        if (!reportResponse.ok) {
          const errorText = await reportResponse.text();
          console.error('[email] Email API failed:', errorText);
          throw new Error(`Email API failed: ${reportResponse.status} - ${errorText}`);
        } else {
          const responseData = await reportResponse.json() as unknown;
          console.log('[email] Email API success response:', responseData);
          console.log('[email] Auto-email sent successfully to:', email);
        }
      } else {
        throw new Error('Failed to generate PDF report - pdfBase64 is empty');
      }
    } catch (error) {
      console.error('[email] Auto-email error:', error);
      throw error;
    }
  };

  return (
    <div className="text-gray-100 p-2 sm:p-4 md:p-6 lg:p-8">
      <div className="max-w-5xl mx-auto rounded-lg border border-gray-800 bg-gray-850 p-2 sm:p-4 md:p-6">
        {/* Top bar removed per request */}

        {/* Add ChartGenerator at the top level */}
        <ChartGenerator answers={answers} onChartGenerated={setChartImageBase64} />
        
        {/* Show submission status */}
        {isSubmitting && (
          <div className="fixed top-4 right-4 bg-blue-600 text-white px-4 py-2 rounded shadow-lg">
            Saving your results...
          </div>
        )}
        
        {submitError && (
          <div className="fixed top-4 right-4 bg-red-600 text-white px-4 py-2 rounded shadow-lg">
            {submitError}
          </div>
        )}

        {/* Contact Form Modal */}
        {showContactForm && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
            <div className="bg-gray-800 text-gray-100 rounded-lg p-6 sm:p-8 max-w-md w-full mx-4 border border-gray-700 shadow-2xl">
              {updateSuccess ? (
                <>
                  <h2 className="text-2xl font-bold mb-3 text-center text-[#59B9C1]">Thank You!</h2>
                  <p className="text-gray-200 text-center">Your report is on its way. This window will close shortly.</p>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-bold mb-3 text-[#59B9C1] text-center">Would you like to receive your results?</h2>
                  <p className="text-gray-200 mb-2 text-center">
                    {userInfo?.name || userInfo?.email ? 
                      'Update your contact details if you would like to receive a copy of your results. This is completely optional.' :
                      'Enter your name and email to receive a copy of your results. This is completely optional.'
                    }
                  </p>
                  <p className="text-xs text-gray-400 mb-4 text-center">
                    By submitting, you agree to our{' '}
                    <a href="https://www.datajukebox.com/data-privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#59B9C1]">Privacy Policy</a>.
                  </p>
                  <form onSubmit={handleContactSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-300">Name</label>
                      <input
                        type="text"
                        id="name"
                        value={contactInfo.name}
                        onChange={handleContactChange}
                        className="mt-1 block w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-[#59B9C1] focus:border-[#59B9C1] sm:text-sm text-gray-100"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-300">Email</label>
                      <input
                        type="email"
                        id="email"
                        value={contactInfo.email}
                        onChange={handleContactChange}
                        className={`mt-1 block w-full px-3 py-2 bg-gray-800 border ${emailValid ? 'border-gray-700' : 'border-red-500'} rounded-md shadow-sm focus:outline-none focus:ring-[#59B9C1] focus:border-[#59B9C1] sm:text-sm text-gray-100`}
                      />
                      {!emailValid && <p className="mt-2 text-sm text-red-400">Please enter a valid email address.</p>}
                    </div>

                    {updateError && (
                      <div className="text-red-400 text-sm">{updateError}</div>
                    )}

                    <div className="flex justify-end space-x-3 mt-4">
                      <button
                        type="button"
                        onClick={() => setShowContactForm(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-700 rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 focus:ring-offset-gray-900"
                      >
                        Skip
                      </button>
                      <button
                        type="submit"
                        disabled={isUpdatingContact || (!!contactInfo.email && !emailValid)}
                        className="px-4 py-2 text-sm font-medium text-white bg-[#59B9C1] rounded-md hover:bg-[#4aa7ae] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#59B9C1] focus:ring-offset-gray-900 disabled:opacity-50"
                      >
                        {isUpdatingContact ? 'Saving...' : 'Save & Continue'}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        )}

        {/* Main Container */}
        <div className="w-full mx-auto my-4 max-w-3xl rounded-xl bg-gray-800 p-4 sm:p-6 md:p-8 border border-gray-700 flex flex-col items-center">
          {/* Title */}
          <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#59B9C1] mb-4 text-center">Your Results</h3>

          {/* Graph Section */}
          <div className="relative w-full flex justify-center mb-4 sm:mb-6">
            <div className="w-full max-w-[400px] sm:max-w-[500px] md:max-w-[600px] aspect-square">
              <SpiderChart answers={answers} />
            </div>
          </div>

          {/* Score Section */}
          <div className="text-center mt-2 w-full">
            <h4 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-100 mb-2">Your Overall Score</h4>
            <p className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#59B9C1] mb-2">{overallRating}%</p>
            <p className="text-xs sm:text-sm text-gray-300 max-w-2xl mx-auto">
              Note: This report compares your scores against the industry average. Leading pharmaceutical companies often aim towards best practice (100% in all areas) to maximize the benefits of HCP personalisation.
            </p>
          </div>
        </div>


        {/* Thank You Message and Actions */}
        <div className="mx-auto my-4 max-w-3xl rounded-xl bg-gray-800 p-4 sm:p-6 md:p-8 border border-gray-700">
          <div className="text-center mb-6">
            <h3 className="mb-4 text-xl sm:text-2xl font-semibold text-gray-100">Thank You for Your Participation</h3>
            <p className="text-gray-300 text-sm sm:text-base">
              Your assessment has been completed successfully. We appreciate your time and participation in the HCP Personalization Benchmark.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col items-center justify-center gap-3 sm:gap-4 text-center">
            {/* Top row: Take New Assessment, Learn More, Contact */}
            <div className="flex flex-col w-full gap-3 sm:gap-4 md:flex-row md:items-center md:justify-center justify-center">
              <button
                className="w-full max-w-xs rounded-lg bg-[#59B9C1] px-4 sm:px-6 py-3 font-semibold text-white transition hover:bg-[#4aa7ae] md:w-auto"
                onClick={handleRestart}
              >
                Take New Assessment
              </button>
              <a
                className="inline-flex w-full max-w-xs items-center justify-center rounded-lg border border-gray-600 bg-transparent px-4 sm:px-6 py-3 text-center text-gray-200 transition hover:bg-gray-700/40 md:w-auto"
                onClick={() => router.push('/case-studies')}
              >
                <span className="font-semibold">Learn More</span>
              </a>
              <button
                className="inline-flex w-full max-w-xs items-center justify-center rounded-lg border border-[#59B9C1] bg-transparent px-4 sm:px-6 py-3 text-center font-semibold text-[#59B9C1] transition hover:bg-[#59B9C1]/10 md:w-auto"
                onClick={() => router.push('/contact')}
              >
                <span className="font-semibold">Contact</span>
              </button>
            </div>

            {/* Bottom row: Download PDF Report, Download Whitepaper */}
            <div className="flex flex-col w-full gap-3 sm:gap-4 md:flex-row md:items-center md:justify-center justify-center">
              <button
                type="button"
                className="w-full max-w-xs rounded-lg bg-[#59B9C1] px-4 sm:px-6 py-3 font-semibold text-white transition hover:bg-[#4aa7ae] md:w-auto"
                onClick={() => void handleDownloadPDF()}
              >
                Download PDF Report
              </button>
              <button
                type="button"
                className="w-full max-w-xs rounded-lg bg-[#59B9C1] px-4 sm:px-6 py-3 font-semibold text-white transition hover:bg-[#4aa7ae] md:w-auto"
                onClick={handleDownloadWhitepaper}
              >
                Download Whitepaper
              </button>
            </div>
          </div>
        </div>
      </div>
      {pdfGateOpen ? (
        <DemoLeadCaptureModal
          onSubmit={(data) => void handlePdfIdentitySubmit(data)}
          onSkip={() => setPdfGateOpen(false)}
        />
      ) : null}
    </div>
  );
}
